import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Table, Modal, FormGroup } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

// Actions / slices
import { fetchComprobantes, fetchComprobanteByNroWithDetails } from '../../reducers/comprobante_slice';
import { fetchPlanCuentas } from '../../reducers/planCuentas_slice';

// UI
import ActionButton from '../UI/Buttons/ActionButton';
import CustomPrintModal from '../UI/CustomPrintModalC';
import ComprobanteForm from './ComprobanteForm';

// Utils
import { fechaActualBoliviaOnly, formatFecha, formatCurrency } from '../../util/valueCalculator';

// ==========================================================
//  FECHAS: PARSEO LOCAL “ANTI-UTC BUG”
//  - "YYYY-MM-DD" => Date LOCAL (00:00)
//  - ISO con hora => Date nativo, pero normalizable a día local cuando haga falta
// ==========================================================
const isYMD = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

const parseYMDLocalStart = (ymd) => {
  if (!ymd || !isYMD(ymd)) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d, 0, 0, 0, 0); // ✅ LOCAL
  return isNaN(dt.getTime()) ? null : dt;
};

const parseAnyToDate = (val) => {
  if (!val) return null;

  // ✅ Si viene como YYYY-MM-DD, NO usar new Date("YYYY-MM-DD") (eso es UTC)
  if (isYMD(val)) return parseYMDLocalStart(val);

  // ✅ Si viene con hora (ISO) o Date, usar Date nativo
  const dt = new Date(val);
  return isNaN(dt.getTime()) ? null : dt;
};

const endOfLocalDay = (val) => {
  if (!val) return null;

  // Si es YYYY-MM-DD => fin del día LOCAL de ese mismo día
  if (isYMD(val)) {
    const [y, m, d] = val.split('-').map(Number);
    const dt = new Date(y, m - 1, d, 23, 59, 59, 999);
    return isNaN(dt.getTime()) ? null : dt;
  }

  // Si trae hora => tomamos el día LOCAL de esa fecha, y lo llevamos al fin de día LOCAL
  const dt = parseAnyToDate(val);
  if (!dt) return null;
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 23, 59, 59, 999);
};

const getYearFromYMD = (ymd) => {
  if (!ymd || typeof ymd !== 'string' || ymd.length < 4) return null;
  const y = parseInt(ymd.slice(0, 4), 10);
  return Number.isFinite(y) ? y : null;
};

// Fuerza año manteniendo MM-DD si es posible (maneja 29/02)
const forceYearKeepMMDD = (ymd, targetYear) => {
  if (!ymd || !targetYear) return ymd;
  if (!isYMD(ymd)) return ymd; // solo ajustamos si es YYYY-MM-DD

  const mm = ymd.slice(5, 7);
  const dd = ymd.slice(8, 10);
  const candidate = `${targetYear}-${mm}-${dd}`;

  const d = parseYMDLocalStart(candidate);
  if (d) return candidate;

  if (`${mm}-${dd}` === '02-29') return `${targetYear}-02-28`;
  return `${targetYear}-12-31`;
};

const ComprobanteList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ===== Store =====
  const comprobantes = useSelector((state) => state.comprobante.comprobantes || []);
  const cuentas = useSelector((state) => state.planCuentas?.planCuentas || []);

  // ===== UI / State =====
  const [sortedComprobantes, setSortedComprobantes] = useState([]);
  const [isAscending, setIsAscending] = useState(true);
  const [searchNroComprobante, setSearchNroComprobante] = useState('');
  const [fechaInicial, setFechaInicial] = useState('');
  const [fechaFinal, setFechaFinal] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [currentComprobante, setCurrentComprobante] = useState(null);
  const [newNroComprobante, setNewNroComprobante] = useState('');

  // ✅ fecha/gestión de trabajo para crear comprobante desde la lista
  const [fechaTrabajo, setFechaTrabajo] = useState('');
  const [gestionTrabajo, setGestionTrabajo] = useState(new Date().getFullYear());

  // Print
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printContent, setPrintContent] = useState({ head: [], body: [] });
  const [title1, setTitle1] = useState('');
  const [title2, setTitle2] = useState('');
  const [title3, setTitle3] = useState('');
  const [title4, setTitle4] = useState('');
  const [title5, setTitle5] = useState('');
  const [totalDebeStr, setTotalDebeStr] = useState('');
  const [totalHaberStr, setTotalHaberStr] = useState('');

  const currentYear = new Date().getFullYear();

  // ===== Carga inicial =====
  useEffect(() => {
    if (!cuentas?.length) dispatch(fetchPlanCuentas());

    dispatch(fetchComprobantes()).then((response) => {
      const data = response?.payload || [];
      // sort desc por número
      const ordenados = [...data].sort((a, b) => {
        const nroA = parseInt((a.nrocomprobante || '0-0').split('-')[1], 10) || 0;
        const nroB = parseInt((b.nrocomprobante || '0-0').split('-')[1], 10) || 0;
        return nroB - nroA;
      });
      setSortedComprobantes(ordenados);

      // fechas por defecto hoy
      const hoy = fechaActualBoliviaOnly(); // "YYYY-MM-DD"
      setFechaInicial((prev) => prev || hoy);
      setFechaFinal((prev) => prev || hoy);
    });
  }, [dispatch, cuentas?.length]);

  useEffect(() => {
  const y = getYearFromYMD(fechaInicial) || new Date().getFullYear();
  setGestionTrabajo(y);
}, [fechaInicial]);
  // ==========================================================
  // CONTROL FUERTE DEL RANGO:
  // - Desde y Hasta misma gestión (mismo año)
  // - Hasta >= Desde
  // ==========================================================
  const normalizeRange = (desde, hasta, showAlerts = true) => {
    let fIni = desde || '';
    let fFin = hasta || '';

    const yIni = getYearFromYMD(fIni);
    const yFin = getYearFromYMD(fFin);

    // Misma gestión
    if (yIni && yFin && yFin !== yIni) {
      fFin = forceYearKeepMMDD(fFin, yIni);
      if (showAlerts) {
        Swal.fire(
          'Atención',
          'La fecha "Hasta" debe estar en la misma gestión que "Desde". Se ajustó automáticamente.',
          'info'
        );
      }
    }

    // Si falta una, igualar
    if (fIni && !fFin) fFin = fIni;
    if (!fIni && fFin) fIni = fFin;

    // Hasta >= Desde (comparación robusta local)
    const dIni = parseYMDLocalStart(fIni);
    const dFin = parseYMDLocalStart(fFin);
    if (dIni && dFin && dFin < dIni) {
      fFin = fIni;
      if (showAlerts) {
        Swal.fire(
          'Atención',
          'Las fechas se ajustaron automáticamente.',
          'info'
        );
      }
    }

    return { fIni, fFin };
  };

  const handleChangeFechaInicial = (value) => {
    const { fIni, fFin } = normalizeRange(value, fechaFinal, true);
    setFechaInicial(fIni);
    setFechaFinal(fFin);
  };

  const handleChangeFechaFinal = (value) => {
    const { fIni, fFin } = normalizeRange(fechaInicial, value, true);
    setFechaInicial(fIni);
    setFechaFinal(fFin);
  };

  // ===== Orden manual por número =====
  const handleSortByNumber = () => {
    const sorted = [...sortedComprobantes].sort((a, b) => {
      if (isAscending) return (a.nrocomprobante || '').localeCompare(b.nrocomprobante || '');
      return (b.nrocomprobante || '').localeCompare(a.nrocomprobante || '');
    });
    setIsAscending(!isAscending);
    setSortedComprobantes(sorted);
  };

  // ===== Filtrado reactivo por rango + número =====
  const filteredComprobantes = useMemo(() => {
    const norm = normalizeRange(fechaInicial, fechaFinal, false);

    // ✅ Desde = inicio del día local
    const dIni = parseYMDLocalStart(norm.fIni);

    // ✅ Hasta = fin del día local (23:59:59.999)
    const dFin = endOfLocalDay(norm.fFin);

    return sortedComprobantes.filter((c) => {
      const matchNro = searchNroComprobante
        ? (c.nrocomprobante || '').includes(searchNroComprobante)
        : true;

      // ✅ Fecha del registro: soporta DATEONLY "YYYY-MM-DD" o ISO con hora
      const dRow = parseAnyToDate(c.fecha);

      const matchFecha =
        (!dIni || (dRow && dRow >= dIni)) &&
        (!dFin || (dRow && dRow <= dFin));

      return matchNro && matchFecha;
    });
  }, [sortedComprobantes, searchNroComprobante, fechaInicial, fechaFinal]);

  // ===== Exportar a Excel (solo lo visible) =====
  const handleExport = () => {
    const exportData = filteredComprobantes.map((comp) => ({
      Número: comp.nrocomprobante,
      Glosa: comp.glosa,
      Fecha: comp.fecha,
      Tipo: comp.tipo_comprobante,
      Estado: comp.estado,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Comprobantes');
    XLSX.writeFile(workbook, 'comprobantes.xlsx');
  };

  // ===== Crear / Editar =====
  const handleCreate = () => {
    // ✅ Gestión de trabajo = año del "Desde"
    const yearTrabajo = getYearFromYMD(fechaInicial) || currentYear;

    // ✅ Fecha de trabajo:
    // - si es gestión pasada => 31/12 de esa gestión
    // - si es gestión actual => fechaInicial (o hoy)
    const hoy = fechaActualBoliviaOnly();
    const fechaWork = (yearTrabajo < currentYear)
      ? `${yearTrabajo}-12-31`
      : (fechaInicial || hoy);

    setGestionTrabajo(yearTrabajo);
    setFechaTrabajo(fechaWork);

    // ✅ Nuevo número basado en la gestión de trabajo
    const delAnio = comprobantes.filter((c) => (c.nrocomprobante || '').startsWith(`${yearTrabajo}-`));
    const ultimo = delAnio.reduce((max, comp) => {
      const num = parseInt((comp.nrocomprobante || '0-0').split('-')[1], 10) || 0;
      return num > max ? num : max;
    }, 0);

    const nuevo = (ultimo + 1).toString().padStart(5, '0');
    setNewNroComprobante(`${yearTrabajo}-${nuevo}`);

    setCurrentComprobante(null);
    setShowModal(true);
  };

  const handleEdit = (comprobante) => {
    setCurrentComprobante(comprobante);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentComprobante(null);
    setNewNroComprobante('');
    setFechaTrabajo('');
    setGestionTrabajo(new Date().getFullYear());
  };

  const handleBack = () => navigate('/admin');

  // ==========================================================
  // IMPRIMIR LISTA (lo filtrado)
  // ==========================================================
  const handleImprimirLista = () => {
    if (!filteredComprobantes.length) {
      Swal.fire('Atención', 'No hay comprobantes para imprimir con el filtro actual.', 'info');
      return;
    }

    const filas = filteredComprobantes.map((comp, idx) => ([
      idx + 1,
      comp.nrocomprobante,
      comp.glosa || '',
      formatFecha(comp.fecha),
      comp.tipo_comprobante || '',
      comp.estado || '',
    ]));

    setPrintContent({
      head: [['#', 'Número', 'Glosa', 'Fecha', 'Tipo', 'Estado']],
      body: filas,
    });

    setTitle1('LISTA DE COMPROBANTES');
    setTitle2(`Rango: ${fechaInicial ? formatFecha(fechaInicial) : '—'} — ${fechaFinal ? formatFecha(fechaFinal) : '—'}`);
    setTitle3('');
    setTitle4(`Total registros: ${filteredComprobantes.length}`);
    setTitle5('');

    setTotalDebeStr('');
    setTotalHaberStr('');

    setShowPrintModal(true);
  };

  // ==========================================================
  // IMPRIMIR CONSOLIDADO (RANGO)
  // ==========================================================
  const handleImprimirConsolidadoRango = async () => {
    if (!fechaInicial || !fechaFinal) {
      Swal.fire('Atención', 'Debes seleccionar un rango de fechas.', 'info');
      return;
    }
    if (!filteredComprobantes.length) {
      Swal.fire('Atención', 'No hay comprobantes en el rango seleccionado.', 'info');
      return;
    }

    try {
      const detallesPorComp = await Promise.all(
        filteredComprobantes.map(async (c) => {
          const res = await dispatch(fetchComprobanteByNroWithDetails(c.nrocomprobante));
          return res?.payload?.detalles || [];
        })
      );
      const todosDetalles = detallesPorComp.flat();

      const mapa = new Map();
      for (const d of todosDetalles) {
        const codigo = d.codigo_cuenta || '';
        if (!codigo) continue;
        const prev = mapa.get(codigo) || {
          codigo_cuenta: codigo,
          nombreCuenta: d?.nombreCuenta || '',
          debe: 0,
          haber: 0,
        };
        prev.debe += Number(d.debe || 0);
        prev.haber += Number(d.haber || 0);
        mapa.set(codigo, prev);
      }

      const agregados = Array.from(mapa.values()).map((x) => {
        const cuenta = cuentas.find(
          (cc) => cc.codigo_cuenta === x.codigo_cuenta && cc.tipo_cuenta === 'DETALLE'
        );
        return {
          codigo_cuenta: x.codigo_cuenta,
          nombreCuenta: cuenta ? cuenta.nombre_cuenta : x.nombreCuenta || 'CUENTA NO ENCONTRADA',
          glosa: 'Consolidado rango',
          debe: Number(x.debe || 0),
          haber: Number(x.haber || 0),
        };
      });

      agregados.sort((a, b) => a.codigo_cuenta.localeCompare(b.codigo_cuenta));

      let totalDebe = 0;
      let totalHaber = 0;
      const filas = agregados.map((row, idx) => {
        totalDebe += row.debe;
        totalHaber += row.haber;
        return [
          idx + 1,
          row.codigo_cuenta,
          row.nombreCuenta,
          row.glosa,
          formatCurrency(row.debe),
          formatCurrency(row.haber),
        ];
      });

      setPrintContent({
        head: [['', 'Código', 'Nombre de Cuenta', 'Glosa', 'Debe', 'Haber']],
        body: filas,
      });

      setTitle1('CONSOLIDADO DE COMPROBANTES (RESPALDO)');
      setTitle2(`Rango: ${formatFecha(fechaInicial)} — ${formatFecha(fechaFinal)}`);
      setTitle3('');
      setTitle4('Glosa: Comprobante diario (consolidado del rango)');
      setTitle5('');

      setTotalDebeStr(formatCurrency(totalDebe));
      setTotalHaberStr(formatCurrency(totalHaber));

      if (Math.abs(totalDebe - totalHaber) > 0.01) {
        Swal.fire('Aviso', 'El consolidado del rango no está balanceado (Debe ≠ Haber).', 'warning');
      }

      setShowPrintModal(true);
    } catch (e) {
      Swal.fire('Error', 'No se pudo preparar el consolidado para imprimir.', 'error');
    }
  };

  return (
    <div className="list-container list1" style={{ maxHeight: 'none', height: 'auto', overflow: 'visible' }}>
      <h2 className="mb-4">Gestión Comprobantes: {gestionTrabajo}</h2>

      <div className="toolbar mb-3 d-flex justify-content-between">
        <div className="left-controls d-flex gap-2 align-items-end">
          <FormGroup className="me-2">
            <label>Nro Comprobante</label>
            <input
              type="text"
              className="form-control"
              value={searchNroComprobante}
              onChange={(e) => setSearchNroComprobante(e.target.value)}
              placeholder="Buscar por número"
            />
          </FormGroup>

          <FormGroup className="me-2">
            <label>Desde</label>
            <input
              type="date"
              className="form-control"
              value={fechaInicial}
              onChange={(e) => handleChangeFechaInicial(e.target.value)}
            />
          </FormGroup>

          <FormGroup className="me-2">
            <label>Hasta</label>
            <input
              type="date"
              className="form-control"
              value={fechaFinal}
              onChange={(e) => handleChangeFechaFinal(e.target.value)}
              min={fechaInicial || undefined}
            />
          </FormGroup>
        </div>

        <div className="right-buttons">
          <button
            title="Imprimir lista de comprobantes"
            onClick={handleImprimirLista}
            className="icon-button btn-transparent"
            disabled={filteredComprobantes.length === 0}
          >
            <i className="bi bi-printer"></i> Lista
          </button>

          <button
            title="Exportar lista de comprobantes (Excel)"
            onDoubleClick={handleExport}
            className="icon-button btn-transparent"
            disabled={filteredComprobantes.length === 0}
          >
            <i className="bi bi-file-spreadsheet"></i> Exportar
          </button>

          <button
            title="Imprimir Consolidado (Rango)"
            onClick={handleImprimirConsolidadoRango}
            className="icon-button btn-transparent"
            disabled={!fechaInicial || !fechaFinal || filteredComprobantes.length === 0}
          >
            <i className="bi bi-printer"></i> Consolidado
          </button>

          <ActionButton icon="plus" outline onClick={handleCreate}>
            Nuevo Comprobante
          </ActionButton>
        </div>
      </div>

      {filteredComprobantes.length === 0 ? (
        <p>No hay comprobantes disponibles.</p>
      ) : (
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 220px)' }}>
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={handleSortByNumber}>
                  Número {isAscending ? '▲' : '▼'}
                </th>
                <th>Glosa</th>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Estado</th>
                <th style={{ width: '120px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredComprobantes.map((comp, idx) => (
                <tr key={idx}>
                  <td>{comp.nrocomprobante}</td>
                  <td>{comp.glosa}</td>
                  <td>{comp.fecha}</td>
                  <td>{comp.tipo_comprobante}</td>
                  <td>{comp.estado}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        title="Editar"
                        onClick={() => handleEdit(comp)}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>

                      <Button
                        variant="outline-secondary"
                        size="sm"
                        title="Eliminar (deshabilitado)"
                        disabled
                        onClick={() => {}}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="xl"
        backdrop="static"
        className="custom-modal-width"
      >
        <Modal.Header closeButton>
          <Modal.Title>{currentComprobante ? 'Editar Comprobante' : 'Nuevo Comprobante'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ComprobanteForm
            currentComprobante={currentComprobante}
            nroComprobanteGenerado={newNroComprobante}
            handleClose={handleCloseModal}
            // ✅ SOLO ESTO: fecha/gestión de trabajo desde el List
            fechaTrabajo={fechaTrabajo}
            gestionTrabajo={gestionTrabajo}
          />
        </Modal.Body>
      </Modal>

      {showPrintModal && (
        <CustomPrintModal
          show={showPrintModal}
          handleClose={() => setShowPrintModal(false)}
          title1={title1}
          title2={title2}
          title3={title3}
          title4={title4}
          title5={title5}
          content={printContent}
          total1={totalDebeStr}
          total2={totalHaberStr}
        />
      )}
    </div>
  );
};

export default ComprobanteList;
